/**
 * Step 6 — Confirmation / ticket display.
 *
 * After `mobile-ticket/create` returns we render the assigned `code` as the
 * hero element on this screen. The user can copy it, print the page, jump
 * to "My tickets" (if logged in), or start a brand-new booking.
 *
 * This component handles three states:
 *   - creating: showing a spinner while the request is in flight
 *   - error: surfacing a retry option
 *   - success: the actual ticket card
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { pickAddress, pickTranslation } from "../../helper/bookingApi";
import {
  EmptyState,
  RetryButton,
  StepCard,
  StepFooter,
  StepHeader,
  STEP_PAD,
} from "./BookingPrimitives";
import { formatDayTimeLabel, parseSlotDate } from "./bookingFormat";

function formatDateTime(value, lang, t) {
  if (!value) return "";
  const d = value instanceof Date ? value : parseSlotDate(value);
  if (!d) return String(value);
  return formatDayTimeLabel(d, lang, t);
}

function SummaryRow({ palette, label, value, icon }) {
  if (!value) return null;

  return (
    <div
      className="d-flex align-items-start gap-2"
      style={{ padding: "10px 0", borderBottom: palette.border }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: 8,
          background: palette.accentSoft,
          color: palette.accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1rem",
        }}
      >
        <i className={icon} aria-hidden="true" />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: "0.78rem",
            color: palette.textMuted,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {label}
        </div>
        <div
          className="fw-600"
          style={{
            color: palette.text,
            fontSize: "0.95rem",
            wordBreak: "break-word",
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function formatServiceNames(service, lang) {
  const services = Array.isArray(service) ? service.filter(Boolean) : service ? [service] : [];
  return services.map((item) => pickTranslation(item, lang)).filter(Boolean).join(", ");
}

export function ConfirmStep({
  palette,
  lang,
  state,
  ticket,
  error,
  industry,
  organization,
  branch,
  service,
  slot,
  email,
  clientName,
  clientComment,
  isAuthenticated,
  onRetry,
  onNewBooking,
  onReopenSuccess,
  ticketsHref,
  onRegister,
  onRecommendedSlotSelect,
  onRecommendedRetry,
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [selectedSuggestedSlot, setSelectedSuggestedSlot] = useState(null);
  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  const code = ticket?.code || "";
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch (_e) {
      const el = document.createElement("textarea");
      el.value = code;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
        setCopied(true);
      } catch (__e) {
        /* noop */
      }
      document.body.removeChild(el);
    }
  };

  const summary = useMemo(
    () => ({
      industry: industry ? pickTranslation(industry, lang) : "",
      organization: organization ? pickTranslation(organization, lang) : "",
      branch: branch
        ? `${pickTranslation(branch, lang)}${
            pickAddress(branch) ? ` — ${pickAddress(branch)}` : ""
          }`
        : "",
      service: formatServiceNames(service, lang),
      datetime: slot ? formatDateTime(slot.date || slot.raw, lang, t) : "",
    }),
    [industry, organization, branch, service, slot, lang, t],
  );

  if (state === "creating") {
    return (
      <StepCard palette={palette}>
        <StepHeader
          palette={palette}
          title={t("booking.confirm.creating")}
          subtitle={t("booking.common.loading")}
        />
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ padding: `40px ${STEP_PAD.x}` }}
        >
          <div
            className="spinner-border"
            style={{ color: palette.accent }}
            role="status"
            aria-hidden="true"
          />
        </div>
      </StepCard>
    );
  }

  if (state === "error") {
    // The backend uses HTTP 400 + a localized `message` for business errors
    // (e.g. "Հնարավոր է պատվիրել առավելագույնը 2 կտրոն" — max 2 tickets).
    // Retrying the same request can't fix those, so we hide the retry CTA
    // when we have a backend message and route the user to the next useful
    // action instead (view tickets / new booking).
    const backendMessage = error?.message;
    const statusCode = error?.statusCode;
    const isBusinessError =
      Boolean(backendMessage) && statusCode >= 400 && statusCode < 500;

    return (
      <StepCard palette={palette}>
        <StepHeader
          palette={palette}
          title={
            isBusinessError ? backendMessage : t("booking.confirm.createError")
          }
          subtitle={
            isBusinessError
              ? t("booking.confirm.errorBusinessHint")
              : backendMessage || t("booking.common.errorGeneric")
          }
        />
        <div style={{ padding: `16px ${STEP_PAD.x} 4px ${STEP_PAD.x}` }}>
          {error?.type === "recommended" ? (
            <div>
              <EmptyState
                palette={palette}
                icon="las la-clock"
                title={error.message}
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: 10,
                  marginTop: 16,
                }}
              >
                {error.suggestions?.map((item) => {
                  const isSelected = selectedSuggestedSlot === item;

                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setSelectedSuggestedSlot(item)}
                      className="pill fw-600"
                      style={{
                        background: isSelected
                          ? "rgb(0, 163, 125)"
                          : palette.surfaceAlt,
                        border: isSelected
                          ? "2px solid rgb(0, 163, 125)"
                          : palette.border,
                        color: isSelected ? "#fff" : palette.text,
                        padding: "10px 14px",
                      }}
                    >
                      {item}
                    </button>
                  );
                })}

                <button
                  type="button"
                  disabled={!selectedSuggestedSlot}
                  onClick={() => onRecommendedRetry?.(selectedSuggestedSlot)}
                  className="pill fw-500"
                  style={{
                    background: "transparent",
                    border: palette.border,
                    color: palette.text,
                    padding: "8px 18px",
                  }}
                >
                  Ամրագրել ընտրված ժամը
                </button>
              </div>
            </div>
          ) : (
            <EmptyState
              palette={palette}
              icon={
                isBusinessError
                  ? "las la-info-circle"
                  : "las la-exclamation-circle"
              }
              title={
                isBusinessError
                  ? backendMessage
                  : t("booking.errors.createTicket")
              }
              action={
                isBusinessError ? null : (
                  <RetryButton palette={palette} onClick={onRetry} />
                )
              }
            />
          )}
        </div>
        <StepFooter palette={palette}>
          <button
            type="button"
            onClick={onNewBooking}
            className="pill fw-500"
            style={{
              background: "transparent",
              border: palette.border,
              color: palette.text,
              padding: "8px 18px",
            }}
          >
            <i className="las la-redo me-1" aria-hidden="true" />
            {t("booking.confirm.actions.newTicket")}
          </button>
          {ticketsHref ? (
            <Link
              to={ticketsHref}
              className="pill fw-600 d-inline-flex align-items-center"
              style={{
                background: palette.accent,
                color: "#fff",
                border: "none",
                padding: "10px 24px",
                textDecoration: "none",
              }}
            >
              <i className="las la-list me-1" aria-hidden="true" />
              {t("booking.confirm.actions.viewMyTickets")}
            </Link>
          ) : (
            <span />
          )}
        </StepFooter>
      </StepCard>
    );
  }

  // Success state
  return (
    <StepCard palette={palette}>
      <StepHeader
        palette={palette}
        title={t("booking.confirm.title")}
        subtitle={t("booking.confirm.subtitle")}
      />

      {typeof onReopenSuccess === "function" ? (
        <div
          className="d-flex justify-content-end"
          style={{ padding: `0 ${STEP_PAD.x} 4px ${STEP_PAD.x}` }}
        >
          <button
            type="button"
            onClick={onReopenSuccess}
            className="pill fw-600"
            style={{
              background: palette.accentSoft,
              border: `1px solid ${palette.accentBorder}`,
              color: palette.accent,
              padding: "8px 18px",
            }}
          >
            <i className="las la-eye me-1" aria-hidden="true" />
            {t("booking.confirm.actions.showAgain")}
          </button>
        </div>
      ) : null}

      {/* Big ticket code */}
      <div
        style={{
          padding: `8px ${STEP_PAD.x} 4px ${STEP_PAD.x}`,
        }}
      >
        <div
          style={{
            background: palette.accentSoft,
            border: `2px dashed ${palette.accentBorder}`,
            borderRadius: 18,
            padding: "clamp(16px, 4vw, 20px) clamp(12px, 3vw, 16px)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "0.78rem",
              color: palette.accent,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontWeight: 700,
            }}
          >
            {t("booking.confirm.codeLabel")}
          </div>
          <div
            className="fw-700"
            style={{
              color: palette.accent,
              fontSize: "clamp(2.4rem, 8vw, 3.6rem)",
              lineHeight: 1.1,
              letterSpacing: "0.12em",
              fontVariantNumeric: "tabular-nums",
              margin: "8px 0",
            }}
          >
            {code || "—"}
          </div>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!code}
            className="pill fw-500"
            style={{
              background: copied ? palette.accent : palette.surface,
              color: copied ? "#fff" : palette.accent,
              border: `1px solid ${palette.accentBorder}`,
              padding: "6px 18px",
              fontSize: "0.9rem",
            }}
          >
            <i
              className={copied ? "las la-check me-1" : "las la-copy me-1"}
              aria-hidden="true"
            />
            {copied
              ? t("booking.confirm.actions.copied")
              : t("booking.confirm.actions.copy")}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div style={{ padding: `12px ${STEP_PAD.x} 4px ${STEP_PAD.x}` }}>
        <div
          className="fw-600 mb-1"
          style={{ color: palette.text, fontSize: "1rem" }}
        >
          {t("booking.confirm.summaryTitle")}
        </div>
        <SummaryRow
          palette={palette}
          icon="las la-tag"
          label={t("booking.confirm.summary.industry")}
          value={summary.industry}
        />
        <SummaryRow
          palette={palette}
          icon="las la-building"
          label={t("booking.confirm.summary.organization")}
          value={summary.organization}
        />
        <SummaryRow
          palette={palette}
          icon="las la-map-marker-alt"
          label={t("booking.confirm.summary.branch")}
          value={summary.branch}
        />
        <SummaryRow
          palette={palette}
          icon="las la-clipboard-check"
          label={t("booking.confirm.summary.service")}
          value={summary.service}
        />
        <SummaryRow
          palette={palette}
          icon="las la-calendar-alt"
          label={t("booking.confirm.summary.datetime")}
          value={summary.datetime}
        />
        {clientName ? (
          <SummaryRow
            palette={palette}
            icon="las la-user"
            label={t("booking.confirm.summary.name")}
            value={clientName}
          />
        ) : null}
        {email ? (
          <SummaryRow
            palette={palette}
            icon="las la-envelope"
            label={t("booking.confirm.summary.email")}
            value={email}
          />
        ) : null}
        {clientComment ? (
          <SummaryRow
            palette={palette}
            icon="las la-comment-dots"
            label={t("booking.confirm.summary.comment")}
            value={clientComment}
          />
        ) : null}
      </div>

      {/* Note */}
      <div style={{ padding: `12px ${STEP_PAD.x} 4px ${STEP_PAD.x}` }}>
        <div
          style={{
            background: palette.surfaceAlt,
            border: palette.border,
            borderRadius: 12,
            padding: "10px 14px",
            color: palette.textMuted,
            fontSize: "0.85rem",
          }}
        >
          <i
            className="las la-info-circle me-1"
            style={{ color: palette.accent }}
            aria-hidden="true"
          />
          {isAuthenticated
            ? t("booking.confirm.noteLoggedIn")
            : t("booking.confirm.noteAnonymous")}
        </div>
      </div>

      {!isAuthenticated && typeof onRegister === "function" ? (
        <div style={{ padding: `8px ${STEP_PAD.x} 4px ${STEP_PAD.x}` }}>
          <div
            className="d-flex flex-wrap align-items-center justify-content-between gap-3"
            style={{
              background: palette.accentSoft,
              border: `1px solid ${palette.accentBorder}`,
              borderRadius: 16,
              padding: "12px 14px",
            }}
          >
            <div style={{ flex: "1 1 240px", minWidth: 0 }}>
              <div
                className="fw-700"
                style={{ color: palette.text, fontSize: "0.98rem" }}
              >
                {t("booking.accountPrompt.title")}
              </div>
              <div
                style={{
                  color: palette.textMuted,
                  fontSize: "0.84rem",
                  marginTop: 2,
                }}
              >
                {t("booking.accountPrompt.text")}
              </div>
            </div>
            <button
              type="button"
              onClick={onRegister}
              className="pill fw-600"
              style={{
                background: palette.accent,
                color: "#fff",
                border: "none",
                padding: "9px 18px",
              }}
            >
              <i className="las la-user-plus me-1" aria-hidden="true" />
              {t("booking.accountPrompt.button")}
            </button>
          </div>
        </div>
      ) : null}

      <StepFooter palette={palette}>
        {ticketsHref ? (
          <Link
            to={ticketsHref}
            className="pill fw-500 d-inline-flex align-items-center justify-content-center"
            style={{
              background: "transparent",
              border: palette.border,
              color: palette.text,
              padding: "8px 18px",
              textDecoration: "none",
            }}
          >
            <i className="las la-list me-1" aria-hidden="true" />
            {t("booking.confirm.actions.viewMyTickets")}
          </Link>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={onNewBooking}
          className="pill fw-500"
          style={{
            background: palette.accent,
            color: "#fff",
            border: "none",
            padding: "10px 24px",
          }}
        >
          <i className="las la-plus me-1" aria-hidden="true" />
          {t("booking.confirm.actions.newTicket")}
        </button>
      </StepFooter>
    </StepCard>
  );
}
